import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useStore } from '../../core/utils/hook';
import './style.scss';

// Below this viewport width the widget starts collapsed by default (summary bar only) instead
// of the full list — on a ~375px phone the expanded list was eating most of the screen width and,
// with 2+ jobs stacked, a big chunk of the height too. Matches the desktop-first "always expanded"
// behavior originally asked for (Google Drive style) above this width.
const MOBILE_BREAKPOINT_QUERY = '(max-width: 480px)';

// #17889 follow-up — corner "download manager" widget (Google Drive / Chrome download tray
// style) for background certificate export jobs. Rendered once at the app root (see App.js), next
// to <ReactModal />, so it survives GA navigation — the job list itself lives on groupAdminStore
// for the same reason (see GroupAdminStore.exportJobs).
const ExportJobsWidget = observer(() => {
    const {
        groupAdminStore: { exportJobs }
    } = useStore();

    // Manual override once the GA taps the summary bar — null means "no override yet, follow
    // the viewport default" so resizing (e.g. rotating a tablet) keeps adapting until they've
    // actually interacted with it once.
    const [expandedOverride, setExpandedOverride] = useState(null);
    const [isMobileViewport, setIsMobileViewport] = useState(
        () => window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
    );

    useEffect(() => {
        const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
        const onChange = (e) => setIsMobileViewport(e.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    if (!exportJobs.length) {
        return null;
    }

    const isExpanded = expandedOverride ?? !isMobileViewport;
    const processingCount = exportJobs.filter(j => j.status === 'PENDING' || j.status === 'PROCESSING').length;
    const doneCount = exportJobs.filter(j => j.status === 'COMPLETED').length;
    const errorCount = exportJobs.filter(j => j.status === 'FAILED').length;

    // #17917 (Figma 6155-6657) — while anything is still being built, the bar just reads
    // "ダウンロード中" (fixed), not the old per-job/count summary — that summary is kept as the
    // fallback once nothing is actively processing anymore (all done/failed), a state the Figma
    // reference doesn't cover.
    const summaryText = processingCount > 0
        ? 'ダウンロード中'
        : exportJobs.length === 1
            ? (exportJobs[0].label || '修了証ダウンロード')
            : `修了証ダウンロード（${exportJobs.length}件` +
                (doneCount ? `・完了${doneCount}` : '') +
                (errorCount ? `・失敗${errorCount}` : '') +
                '）';

    return (
        <div className={`export-jobs-widget${isExpanded ? '' : ' is-collapsed'}`}>
            <div
                className='export-jobs-widget-summary'
                role='button'
                aria-expanded={isExpanded}
                onClick={() => setExpandedOverride(!isExpanded)}
            >
                <span className='export-jobs-widget-summary-text'>{summaryText}</span>
                <i className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-up'} export-jobs-widget-summary-toggle`} />
            </div>

            {isExpanded && (
                <div className='export-jobs-widget-list'>
                    {exportJobs.map(job => {
                        const percent = job.totalCount
                            ? Math.min(100, Math.round((job.processedCount / job.totalCount) * 100))
                            : 0;

                        return (
                            <div key={job.jobId} className='export-job-item'>
                                <div className='export-job-item-header'>
                                    {/* #17917 follow-up — no dismiss (✕) button: it used to only hide the entry
                                        client-side while the job kept running server-side regardless (not
                                        cancellable), so a GA dismissing a PENDING/PROCESSING job would silently
                                        lose auto-download when it finished. Removed entirely rather than only
                                        gating it to COMPLETED/FAILED, per explicit product decision. */}
                                    <span className='export-job-item-label' title={job.label}>{job.label || '修了証ダウンロード'}</span>
                                </div>

                                <div className='export-job-item-body'>
                                    {job.status === 'FAILED' ? (
                                        <div className='export-job-item-status export-job-item-status-error'>
                                            <i className='fa-solid fa-circle-exclamation' />
                                            <span>{job.error || 'エクスポートに失敗しました。'}</span>
                                        </div>
                                    ) : job.status === 'COMPLETED' ? (
                                        <div className='export-job-item-status export-job-item-status-done'>
                                            <i className='fa-solid fa-circle-check' />
                                            <span>準備ができました。</span>
                                            <a href={job.downloadUrl} className='export-job-item-download'>
                                                <i className='fa-solid fa-download' /> ダウンロード
                                            </a>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='export-job-item-progress-text'>
                                                {job.status === 'PENDING'
                                                    ? '出力を準備しています…'
                                                    : `ファイル作成中 (${job.processedCount}/${job.totalCount})`}
                                            </div>
                                            <div className='export-job-item-progress-bar'>
                                                <div className='export-job-item-progress-bar-fill' style={{ width: `${percent}%` }} />
                                            </div>
                                            {/* #17917 (Figma 6155-6657) — only while a job is still building; COMPLETED/FAILED
                                                above don't need it (nothing left to wait for / already stopped). */}
                                            <div className='export-job-item-warning'>
                                                <p>※ファイルの作成が完了すると、自動的にダウンロードされます。この処理には数分かかる場合があります。</p>
                                                <p><u>※ダウンロードが完了するまで、ブラウザを閉じたり、ログアウトしたりしないでください。</u></p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

export default ExportJobsWidget;
